/**
 * @copyright (C) 2023, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Heeseok Shin (hsshin@rsupport.com)
 *
 * @description
 *
 * @created at Wed Dec 27 2023
 **/

import { useAtomCallback } from "jotai/utils";
import { useCallback } from "react";

import { useEventHandler } from "@rfice/eventhandler";
import { JotaiStore } from "@/frameworks/libraries/commons";
import { useStompClient } from "@/frameworks/libraries/network";
import { useNotice } from "@/frameworks/core/API";
import { Configure } from "@/frameworks/libraries";

import { NotificationAtomValue, NotificationItem } from "./atom";
import useNotification from "./useNotification";
import useNotificationList, { getKeyByValue } from "./useNotificationList";
import { formatSerializedNormalData } from "./utils";
import { AlarmCategoryType, AlarmLogMessage, AlarmLogMessageValue } from "./types";

type Props = {
	onError: (error: Error) => void;
};

export default function useNotificationManager({ onError }: Props) {
	const { atom } = useNotification();
	const normal = useNotificationList("NORMAL");
	const noticeAPI = useNotice();

	const run = useCallback(async () => {
		try {
			await normal.fetchNextPage().then(({ data }) => {
				if (data) {
					data.data?.content?.forEach((item) => {
						updateData("NORMAL", formatSerializedNormalData(item));
					});
				}
			});

			await noticeAPI.notification.inquiry
				.execute({
					page: 1,
					pageSize: 100
				})
				.then(({ data }) => {
					if (data) {
						data?.content?.forEach((item) => {
							updateData(
								"NOTICE",
								formatSerializedNormalData({
									read: item.read,
									createdTime: item.createdTime,
									type: "NOTICE_NOTIFY",
									payload: {
										readKey: item.id,
										title: item.title,
										link: item.link
									}
								})
							);
						});
					}
				});
		} catch (error) {
			onError(error as Error);
		}
	}, []);

	const handleReadByKey = useAtomCallback<void, [string]>(
		(_, set, logID: string) => {
			set(atom.notification, (prev: NotificationAtomValue) => {
				return new Map(prev!.set(logID, { ...prev.get(logID)!, read: true }));
			});
		},
		{
			store: JotaiStore
		}
	);

	useEventHandler(
		"notificationLogs:readByKey",
		(logID: string) => {
			handleReadByKey(logID);
		},
		[]
	);

	useStompClient(
		{
			onMessageReceived(message) {
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				const _message = message as Message.Frame<AlarmLogMessageValue, any, Message.Options>;
				const messageType = _message.type as AlarmLogMessageValue;
				if (getKeyByValue(AlarmLogMessage, messageType)) {
					const category = formatCategoryType(_message);

					updateData(category, {
						createdTime: new Date(),
						read: _message.payload.sender ? Configure.userID === _message.payload.sender.id : false,
						type: _message.type,
						payload: _message.payload
					});
				}
			}
		},
		[]
	);

	const updateData = useAtomCallback(
		(_, set, category: AlarmCategoryType | "UNKNOWN", args?: NotificationItem) => {
			switch (category) {
				// 'NOTICE' case will handle notifications which passed to distributeNotifiactionByType() and return 'NOTICE', Those notifications belong to tab '공지사항' on UI
				case "NOTICE": {
					set(atom.notificationNotice, (prev) => {
						const next = new Map(prev);
						if (args) {
							next.set(args.payload?.readKey || "", args);
						}
						return next;
					});
					break;
				}
				case "NORMAL": {
					set(atom.notification, (prev) => {
						const next = new Map(prev);
						if (args) {
							next.set(args.payload?.readKey || "", args);
						}
						return next;
					});
					break;
				}
				// 'unknown' case will handle notifications which passed to distributeNotifiactionByType() and return 'unknown', Those notifications will not be displayed on UI
				case "UNKNOWN":
				default: {
					break;
				}
			}
		},
		{
			store: JotaiStore
		}
	);

	return {
		run
	};
}

function formatCategoryType(
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	message: Message.Frame<AlarmLogMessageValue, any, Message.Options>
): AlarmCategoryType | "UNKNOWN" {
	if (message.payload?.notificationCategory) {
		return message.payload.notificationCategory;
	} else {
		switch (message.type) {
			default: {
				return "UNKNOWN";
			}
		}
	}
}
